import { Component, computed, inject, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { ActiveOrganization } from '@models/organization.model';
import { WorkflowModel } from '@models/workflow.model';
import { Dictionary } from '@ngrx/entity/src';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { UiBreadCrumbItem, UiHeaderWithBreadcrumbsComponent } from '@ui';
import { map } from 'rxjs';
import { RunStepsTableComponent } from '../run-steps-table/run-steps-table.component';
import { RunStepsChartComponent } from '../run-steps-chart/run-steps-chart.component';
import { MatIcon } from '@angular/material/icon';
import { syncQueryParam } from 'src/app/helpers/sync-query-param.helper';
import { MatTooltip } from '@angular/material/tooltip';

export enum VisualisationTypes {
  TABLE = 'table',
  BAR_CHART = 'bar_chart',
  LINE_CHART = 'line_chart',
}

@Component({
  selector: 'app-run-steps-page',
  imports: [
    UiHeaderWithBreadcrumbsComponent,
    RunStepsTableComponent,
    RunStepsChartComponent,
    TranslatePipe,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: './run-steps-page.component.html',
  styleUrl: './run-steps-page.component.scss'
})
export class RunStepsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly store$ = inject(Store);

  public visualisationTypeParam = syncQueryParam<string>('visualisationType', { defaultValue: VisualisationTypes.TABLE });

  public visualisationTypes = VisualisationTypes;

  public selectOrganizationSig: Signal<ActiveOrganization> =
    this.store$.selectSignal(OrganizationSelectors.selectActiveOrganization);
  public selectWorkflowsEntities: Signal<Dictionary<WorkflowModel.WorkflowDocument>> =
    this.store$.selectSignal(WorkflowsSelectors.selectWorkflowsEntities);

  public orgIdSig: Signal<string> = computed(() => {
    const orgId = this.selectOrganizationSig().orgId as string;
    return orgId;
  })

  public graphIdSig: Signal<string> = toSignal(
    this.route.paramMap.pipe(
      map(params => params.get('graphId'))
    ),
    { initialValue: null }
  );

  public runIdSig: Signal<string> = toSignal(
    this.route.paramMap.pipe(
      map(params => params.get('runId'))
    ),
    { initialValue: null }
  );

  public breadcrumbsSig: Signal<UiBreadCrumbItem[]> = computed(() => {
    return [
      {
        name: this.translateService.instant('BREADCRUMBS.WORKFLOWS'),
        route: `/${routerSegments.workflows}`,
      },
      {
        name: this.selectWorkflowsEntities()[this.graphIdSig()]?.published?.name ?? '',
        route: `/${routerSegments.workflows}/${this.graphIdSig()}`,
      },
      {
        name: this.translateService.instant('FLOW_CHART.RUN_LIST_TABLE.DIALOG_TITLE'),
        route: `/${routerSegments.workflows}/${this.graphIdSig()}/${routerSegments.runs}`,
      },
      {
        name: this.runIdSig(),
      }
    ];
  })

  public ngOnInit(): void {
    this.store$.dispatch(WorkflowActions.resetWorkflowRunSteps());

    if (!Object.keys(this.selectWorkflowsEntities() ?? {}).length) {
      this.store$.dispatch(
        WorkflowActions.getWorkflows({
          orgId: this.orgIdSig(),
        }),
      );
    }
  }
}
